import { useApiMutation } from "@gladis/api-client";
import { Button, Dialog, Label, SelectField, TextField } from "@gladis/ui";
import { errorSentence, useT } from "@gladis/ui/i18n";
import { useState, type FormEvent, type ReactNode } from "react";

import {
  BankCode,
  LastFour,
  identifierAccessibleLabel,
  identifierLabel,
} from "../../entities/value-objects/card-identifier.js";
import { EXPENSES_KEY, type CardSummaryDto } from "./ExpenseList.js";
import { PaymentCard } from "./PaymentCard.js";

/**
 * Register a payment source, or correct one (FR-035 to FR-040).
 *
 * ==========================================================================================
 * TWO PANES: THE CARD ON THE LEFT, THE FIELDS ON THE RIGHT
 * ==========================================================================================
 *
 * The card fills in as the member types. That is not decoration — it is the only place in the feature
 * where the three things that are easy to get wrong are checkable BEFORE they are saved:
 *
 *   1. WHOSE CARD IT IS. Joint or individual inverts the settle-up: a joint card contributes nothing
 *      to it, an individual one attributes the whole cost to its owner. The badge says "Joint" or the
 *      owner's name, so the member reads the consequence rather than the setting.
 *   2. WHICH OF TWO SIMILAR CARDS THIS IS. Two cards from the same bank are told apart by four digits
 *      and nothing else, and `INGB •••• 4488` is the form the member will be picking from later.
 *   3. WHETHER IT HAS AN IDENTIFIER AT ALL. A card with neither half shows "No account identifier",
 *      which is legitimate and worth seeing before saving rather than discovering afterwards.
 *
 * IT RENDERS THE REAL `PaymentCard`, not a mock-up of one. A hand-drawn rectangle here would be free to
 * drift from the component the page actually uses — different edge treatment, different masking — and a
 * preview that does not match what gets saved is worse than no preview.
 *
 * The preview's monthly total is honest rather than a placeholder, in both directions: a card's total is
 * the sum of the expenses bound to it (FR-046), so a card that does not exist yet reads €0,00 — which is
 * exactly what it will show the moment it is created — and a card being EDITED reads its own real figure,
 * because that figure is a fact about the card rather than about this form.
 *
 * ==========================================================================================
 * THE IDENTIFIER IN THE PREVIEW IS THE DOMAIN'S OWN RULE, IMPORTED
 * ==========================================================================================
 *
 * `identifierLabel` decides four cases — both halves, code only, digits only, neither — and its own
 * comment says it is assembled in one place because "four cases decided in two places become five
 * cases". Formatting `INGB •••• 4488` again in this file to drive a preview is precisely that fifth
 * case, and it would show a member one thing and store another.
 *
 * So the preview calls the same two functions, from `entities/value-objects/`. An intra-package import,
 * so it crosses no boundary the lint rules police — and it pulls in the value objects rather than the
 * package root, which would put the whole domain on the path of a form.
 *
 * IT BUILDS THEM THROUGH `create`, NOT `trusted`, and shows only what validates. A bank code of one
 * character is not shown, because it is not a bank code — the server would refuse it. That makes the
 * preview double as the answer to "is what I typed acceptable", without a second set of rules to say so.
 *
 * ==========================================================================================
 * THE REST
 * ==========================================================================================
 *
 * IT IS A MODAL, and it used to be a panel that appeared above the card set. The reason for changing is
 * what the panel did to the page: opening it pushed the cards down by its own height, so the click that
 * said "add a card" moved everything the member was looking at. A modal takes the same form out of the
 * flow entirely — the page behind it does not move, and closing puts the member back exactly where they
 * were.
 *
 * THE HEADING AND THE SENTENCE ARE PASSED TO `Dialog`, NOT DRAWN HERE. This component no longer renders a
 * frame or an `<h2>` of its own — the dialog supplies both, and rendering them here as well is how a
 * modal ends up with two borders and its title twice. The COPY did not move out of this package: it is
 * expenses' knowledge of what a card is, and it is still written in this file.
 *
 * THE TWO IDENTIFIER HALVES ARE SEPARATE FIELDS, and both are optional. There is no field for a full card or
 * account number anywhere in this form or in the schema behind it — nothing here initiates a payment, so a
 * full number has no use, and the local database is dumped to a plaintext file as part of the normal
 * workflow. Four digits and a short code are enough to tell two cards from the same bank apart, which is the
 * only job the identifier has.
 *
 * `lastFour` is held and sent as a STRING. As a number `0042` arrives as `42`, and no validation downstream
 * can recover digits dropped before anything ran.
 *
 * ==========================================================================================
 * ONE FORM FOR CREATING AND FOR EDITING
 * ==========================================================================================
 *
 * `ExpenseForm` settled this shape and the argument is the same one card fields along: every rule this form
 * applies — the bank code's alphabet, four digits that survive a leading zero, an individual card needing an
 * owner and a joint one refusing to have any — applies identically to an edit. A second form would be a
 * second copy of all of it, and the preview would be the thing that drifted first, since it is the part with
 * no server behind it. `PATCH /api/cards/:id` and `editCard` shipped with 009 and had no caller at all; this
 * is it, and until it existed a card typed with the wrong last four digits could only be archived.
 */
export function CardForm({
  open,
  onClose,
  members,
  editing,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  members: readonly { userId: string; displayName: string }[];
  /**
   * The card being changed, or null/undefined to register a new one.
   *
   * The card ROW rather than an id, for the reason the Expenses page gives: the fields are filled from it,
   * and holding an id would mean looking it up again in a payload that may have refetched in between —
   * which is how a form comes to show a name the member already corrected.
   */
  editing?: CardSummaryDto | null;
  onCreated?: () => void;
}): ReactNode {
  const t = useT();
  const [name, setName] = useState("");
  const [ownership, setOwnership] = useState<"joint" | "individual">("individual");
  /*
   * EMPTY MEANS "WHICHEVER THE PICKER IS SHOWING", RESOLVED BELOW — it is not itself a default.
   *
   * This was `useState(members[0]?.userId ?? "")`, and the bug the live preview found on its first
   * render is worth recording. `members` arrives from a query, so it is `[]` when this component first
   * renders and the initialiser runs exactly once against that empty array — leaving `ownerId` as `""`
   * for good. Meanwhile a `<select>` whose `value` matches no option falls back to displaying its FIRST
   * option, so the picker showed the first member and the state held nothing.
   *
   * Nothing revealed that until the preview asked for the owner's NAME and got "Former member". What the
   * member could not see was the submit: creating an individual card without touching the picker sent
   * `ownerId: ""`, and the refusal that came back described a form that looked correctly filled in.
   *
   * Resolving at read time instead of mirroring in state is what fixes it rather than papering over it:
   * there is now ONE answer to "whose card is this", and the picker, the preview and the request are all
   * reading it. An effect that copied `members[0]` into state when the query landed would restore the
   * two-answer shape and reintroduce this the next time the timing changed.
   */
  const [ownerId, setOwnerId] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [lastFour, setLastFour] = useState("");

  /*
   * THE FIELDS ARE FILLED FROM THE CARD WHEN THE DIALOG OPENS, keyed on which card it opened for.
   *
   * `ExpenseForm`'s three states, and its third is what makes reopening work here too: `undefined` while
   * closed, `null` while creating, an id while editing. With only `id | null`, closing the dialog and
   * reopening it on the SAME card leaves `filledFor` equal to `openedFor`, so the fields are never
   * re-filled — a member who changed the last four digits, cancelled, and opened it again would be shown
   * their abandoned edit as though it had been saved.
   *
   * Keyed on the id rather than on `open` alone, because re-running on every render would fight the
   * member's typing.
   */
  const openedFor = editing?.id ?? null;
  const [filledFor, setFilledFor] = useState<string | null | undefined>(undefined);

  if (!open) {
    if (filledFor !== undefined) setFilledFor(undefined);
  } else if (openedFor !== filledFor) {
    setFilledFor(openedFor);
    if (editing) {
      setName(editing.name);
      setOwnership(editing.isJoint ? "joint" : "individual");
      /*
       * `""` FOR A CARD WHOSE OWNER HAS LEFT, which is the resolution below doing exactly what it was
       * written for rather than a case handled here: `ownerId` is null on such a card, the picker falls
       * back to the first member, and saving is how FR-047's "re-own it and the settle-up starts working
       * again" is actually reached. Copying the null through as a stored `null` would leave the picker
       * showing a name the request would not carry — the two-answer shape this state exists to avoid.
       */
      setOwnerId(editing.ownerId ?? "");
      setBankCode(editing.bankCode ?? "");
      setLastFour(editing.lastFour ?? "");
    } else {
      setName("");
      setOwnership("individual");
      setOwnerId("");
      setBankCode("");
      setLastFour("");
    }
  }

  const selectedOwnerId = ownerId === "" ? (members[0]?.userId ?? "") : ownerId;

  const save = useApiMutation<{ id: string }, Record<string, unknown>>(
    (client, input) =>
      editing
        ? client.patch<{ id: string }>(`/cards/${editing.id}`, input)
        : client.post<{ id: string }>("/cards", input),
    { invalidates: [EXPENSES_KEY] },
  );

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate(
      {
        name,
        ownership,
        /*
         * ON A CREATE a joint card sends no owner at all: sending null would be refused, which is correct
         * and is also a refusal the member could not act on from this form.
         *
         * ON AN EDIT it sends `null`, and that difference is not cosmetic. `PATCH` reads an omitted field
         * as "leave it alone", and `CardEntity.edit` therefore keeps the stored `ownerId` — so making an
         * individual card joint without this would submit "joint" while the owner stayed put, and
         * `validateOwnership` would refuse a joint card that has an owner. The member would be told their
         * card cannot be joint, by a form on which they had just chosen joint.
         */
        ...(ownership === "individual"
          ? { ownerId: selectedOwnerId }
          : editing
            ? { ownerId: null }
            : {}),
        /*
         * The identifier halves follow the same rule for the same reason: omitted means "keep it" on a
         * PATCH, so clearing a field has to be an explicit `null` or a member can never remove four digits
         * they typed in error — which is one of the two things this form was added to make possible.
         */
        ...(bankCode === "" ? (editing ? { bankCode: null } : {}) : { bankCode }),
        ...(lastFour === "" ? (editing ? { lastFour: null } : {}) : { lastFour }),
      },
      {
        onSuccess: () => {
          /*
           * CLEARED ONLY ON A CREATE, where the form stays open for the next card and the previous one's
           * name left in the field would be a trap. An edit closes, and its fields are re-filled from the
           * card the next time it opens.
           */
          if (!editing) {
            setName("");
            setBankCode("");
            setLastFour("");
          }
          onCreated?.();
        },
      },
    );
  }

  /*
   * The two halves as value objects, or null.
   *
   * `unwrapOr(null)` rather than an `isOk()` branch because the failure carries nothing this form wants:
   * a half-typed bank code is not an error to report, it is a preview that has not caught up yet. The
   * field-level refusals a member does need come from the server on submit, in one place.
   */
  const bankCodeVo = bankCode.trim() === "" ? null : BankCode.create(bankCode).unwrapOr(null);
  const lastFourVo = lastFour.trim() === "" ? null : LastFour.create(lastFour).unwrapOr(null);

  const previewCard: CardSummaryDto = {
    id: editing?.id ?? "preview",
    name,
    ownership,
    ownerId: ownership === "individual" ? selectedOwnerId : null,
    // The badge shows the owner's NAME, so the preview has to resolve it the way the server will.
    ownerName:
      ownership === "individual"
        ? (members.find((member) => member.userId === selectedOwnerId)?.displayName ?? null)
        : null,
    identifier: identifierLabel(bankCodeVo, lastFourVo),
    accessibleIdentifier: identifierAccessibleLabel(bankCodeVo, lastFourVo),
    bankCode: bankCodeVo === null ? null : bankCodeVo.toString(),
    lastFour: lastFourVo === null ? null : lastFourVo.toString(),
    /*
     * THE CARD'S OWN FIGURE AND STATE, when there is a card. Both are facts about it rather than about
     * this form — the total is the sum of the expenses bound to it (FR-046), and an archived card is drawn
     * set aside wherever it appears (FR-043) — so a preview that reset them would show the member a
     * different card from the one they opened. €0,00 and "active" remain honest for a card that does not
     * exist yet: that is precisely what it will show the moment it is created.
     */
    monthlyTotalInEuros: editing?.monthlyTotalInEuros ?? 0,
    isJoint: ownership === "joint",
    archivedAt: editing?.archivedAt ?? null,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="wide"
      title={editing ? t("exp.card.editHeading") : t("exp.card.addHeading")}
      description={editing ? t("exp.card.editHint") : t("exp.card.hint")}
    >
      {/*
        STACKED BELOW `lg`, SIDE BY SIDE AT AND ABOVE IT, and the breakpoint is chosen by what the two
        panes need rather than by the usual `md`: the field grid goes two-up at `sm`, so pairing it with
        a 22rem card before about 1024px leaves both columns too narrow to read. Below that the card sits
        centred above the fields, which is the right shape on a phone anyway.

        `lg:sticky` keeps the card in view while a long field list scrolls under it — the dialog's body is
        the scroll container, so this sticks against the header. `lg:self-start` because a stretched flex
        item has nothing to stick within.
      */}
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
        <div className="mx-auto w-full max-w-[22rem] lg:sticky lg:top-0 lg:mx-0 lg:shrink-0">
          {/*
            `aria-hidden`, and this is the one decision here worth arguing.

            Every field already has a label, and the card repeats those same values in a different
            arrangement — so to a screen reader the preview is the form read twice, with the second copy
            arriving before the first. Worse, it updates on every keystroke, and `PaymentCard` contains a
            visually-hidden identifier span written for exactly this kind of reader; live-announcing it
            mid-word is noise, not help.

            What the preview offers is a GLANCE — is that the right badge, are those the right four
            digits — and a glance has no auditory equivalent. Nothing is lost: there is no information on
            the card that is not in a labelled field a few tab stops away.
          */}
          <div aria-hidden="true">
            <PaymentCard card={previewCard} placeholderName={t("exp.card.yourCard")} />
          </div>
        </div>

        <form onSubmit={submit} className="min-w-0 flex-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="card-name">{t("exp.card.name")}</Label>
              <TextField
                id="card-name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                placeholder={t("exp.card.namePlaceholder")}
                required
              />
            </div>

            <div>
              <Label htmlFor="card-ownership">{t("exp.card.whose")}</Label>
              <SelectField
                id="card-ownership"
                value={ownership}
                onChange={(event) => {
                  setOwnership(event.target.value as "joint" | "individual");
                }}
              >
                {/* Joint or individual decides the settle-up entirely, so the wording says what it means. */}
                <option value="individual">{t("exp.card.individual")}</option>
                <option value="joint">{t("exp.card.joint")}</option>
              </SelectField>
            </div>

            {ownership === "individual" ? (
              <div>
                <Label htmlFor="card-owner">{t("exp.card.whoseCard")}</Label>
                <SelectField
                  id="card-owner"
                  value={selectedOwnerId}
                  onChange={(event) => {
                    setOwnerId(event.target.value);
                  }}
                  required
                >
                  {members.map((member) => (
                    <option key={member.userId} value={member.userId}>
                      {member.displayName}
                    </option>
                  ))}
                </SelectField>
              </div>
            ) : null}

            <div>
              <Label htmlFor="card-bank">{t("exp.card.bankCode")}</Label>
              <TextField
                id="card-bank"
                value={bankCode}
                onChange={(event) => {
                  setBankCode(event.target.value.toUpperCase());
                }}
                placeholder="INGB"
                maxLength={11}
              />
            </div>

            <div>
              <Label htmlFor="card-last-four">{t("exp.card.lastFour")}</Label>
              <TextField
                id="card-last-four"
                value={lastFour}
                onChange={(event) => {
                  // Digits only, and never coerced to a number — `0042` must survive.
                  setLastFour(event.target.value.replace(/\D/g, "").slice(0, 4));
                }}
                placeholder="4488"
                inputMode="numeric"
              />
            </div>
          </div>

          {save.error ? (
            <p className="mt-4 text-sm text-danger" role="alert">
              {errorSentence(save.error)}
            </p>
          ) : null}

          {/* Cancel beside Submit, although Escape, the backdrop and the header's close all do the same
              thing. None of those three is visible, and a form the member decided against should have a
              way out that is as findable as the way forward. */}
          <div className="mt-5 flex justify-end gap-3">
            <Button type="button" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            {/*
              THE LABEL SAYS WHICH OPERATION THIS IS. "Add card" on a form that is editing one is the
              wrong sentence at the moment a member commits to it — the same rule `ExpenseForm` states.
            */}
            <Button type="submit" variant="primary" disabled={save.isPending || name.trim() === ""}>
              {save.isPending
                ? editing
                  ? t("common.saving")
                  : t("exp.form.adding")
                : editing
                  ? t("exp.card.save")
                  : t("exp.card.add")}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}
